import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { getItemImageSrc, handleImgError } from '../../../utils/itemUtils';
import Tooltip from '../ItemTooltip';
import Toast from '../../Shared/Toast';
import { Item } from '../../types/Item';
import styles from './EmperorsVault.module.css';

interface VaultListing {
  vaultItemId: number;
  itemId: number;
  tokenPrice: number;
  stock: number;
  maxStock: number;
  item: Item;
}

const CATEGORY_ORDER = ['Weapon', 'Shield'];
const WEAPON_TYPE_ORDER = ['Mace', 'Chain', 'Axe', 'Sword', 'Staff'];

const EmperorsVault: React.FC = () => {
  const { mercenary, setMercenary } = useMercenary();
  const [listings, setListings] = useState<VaultListing[]>([]);
  const [emperorTokens, setEmperorTokens] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [buying, setBuying] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const fetchVault = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/emperors-vault/items', { withCredentials: true });
      setListings(res.data.items);
      setEmperorTokens(res.data.emperorTokens);
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to load the Emperor's Vault.";
      setToast({ msg, ok: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVault();
  }, [fetchVault]);

  const handleBuy = async (vaultItemId: number, itemName: string, tokenPrice: number) => {
    if (emperorTokens < tokenPrice) {
      setToast({ msg: `You need ${tokenPrice} Emperor's Tokens but only have ${emperorTokens}.`, ok: false });
      return;
    }
    setBuying(vaultItemId);
    try {
      const res = await axios.post(`/api/emperors-vault/buy/${vaultItemId}`, {}, { withCredentials: true });
      setEmperorTokens(res.data.emperorTokens);
      // Update stock in local state
      setListings(prev =>
        prev.map(l =>
          l.vaultItemId === vaultItemId ? { ...l, stock: Math.max(0, l.stock - 1) } : l
        )
      );
      // Update mercenary context with new token count
      if (mercenary) {
        setMercenary({ ...mercenary, emperorTokens: res.data.emperorTokens } as any);
      }
      setToast({ msg: `${itemName} has been added to your inventory.`, ok: true });
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Purchase failed. Please try again.';
      setToast({ msg, ok: false });
    } finally {
      setBuying(null);
    }
  };



  // Categorize and sort listings
  const categories = ['All', 'Weapon - 2H', 'Weapon - 1H', 'Shield'];
  const filteredListings = listings.filter(l => {
    if (selectedCategory === 'All') return true;
    if (selectedCategory === 'Weapon - 2H') return l.item.itemCategory === 'Weapon' && l.item.slot === 'twoHand';
    if (selectedCategory === 'Weapon - 1H') return l.item.itemCategory === 'Weapon' && l.item.slot === 'oneHand';
    if (selectedCategory === 'Shield') return l.item.itemCategory === 'Shield';
    return true;
  });

  if (!mercenary) return null;

  return (
    <div className={styles.vaultContainer}>
      <div className={styles.vaultHeader}>
        <div className={styles.titleBlock}>
          <h1 className={styles.vaultTitle}>Emperor's Vault</h1>
          <p className={styles.vaultSubtitle}>
            The finest weapons and shields ever forged, reserved for those who have earned the right to be called Legend.
          </p>
        </div>
        <div className={styles.tokenDisplay}>
          <span className={styles.tokenIcon}>⚜</span>
          <span className={styles.tokenCount}>{emperorTokens}</span>
          <span className={styles.tokenLabel}>Emperor's Tokens</span>
        </div>
      </div>

      <div className={styles.vaultInfo}>
        <div className={styles.infoCard}>
          <strong>How to earn tokens:</strong> Win the Legend Tournament to receive <strong>10 Emperor's Tokens</strong>.
        </div>
        <div className={styles.infoCard}>
          <strong>Bound items:</strong> All Emperor's Vault items are <strong>Bound on Purchase</strong>, they cannot be sold on the Auction House or deposited into a guild vault.
        </div>
        <div className={styles.infoCard}>
          <strong>Stock:</strong> Each item has limited stock.
        </div>
        <div className={styles.priceCard}>
          <span className={styles.priceTag}>2H Weapons: <strong>10 tokens</strong></span>
          <span className={styles.priceTag}>1H Weapons: <strong>5 tokens</strong></span>
          <span className={styles.priceTag}>Shields: <strong>5 tokens</strong></span>
        </div>
      </div>

      <div className={styles.categoryTabs}>
        {categories.map(cat => (
          <button
            key={cat}
            className={`${styles.categoryTab} ${selectedCategory === cat ? styles.activeTab : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={styles.loading}>Entering the vault…</div>
      ) : (
        <div className={styles.itemGrid}>
          {filteredListings.length === 0 && (
            <div className={styles.emptyState}>No items available in this category.</div>
          )}
          {filteredListings.map(listing => {
            const canAfford = emperorTokens >= listing.tokenPrice;
            const inStock = listing.stock > 0;
            const isBuying = buying === listing.vaultItemId;

            return (
              <div
                key={listing.vaultItemId}
                className={`${styles.itemCard} ${!inStock ? styles.soldOut : ''} ${!canAfford && inStock ? styles.cantAfford : ''}`}
              >
                <div className={styles.itemImageWrapper}>
                  <Tooltip item={listing.item} boundNote="Binds on Purchase">
                    <img
                      src={getItemImageSrc(listing.item)}
                      alt={listing.item.name}
                      className={styles.itemImage}
                      onError={handleImgError}
                    />
                  </Tooltip>
                  {!inStock && <div className={styles.soldOutBadge}>SOLD OUT</div>}
                </div>

                <div className={styles.itemInfo}>
                  <h3 className={styles.itemName}>{listing.item.name}</h3>
                  <div className={styles.itemMeta}>
                    <span className={styles.itemType}>
                      {listing.item.slot === 'twoHand' ? '2H ' : listing.item.slot === 'oneHand' ? '1H ' : ''}
                      {listing.item.weaponType || listing.item.itemCategory}
                    </span>
                    {listing.item.requiredLevel && (
                      <span className={styles.itemLevel}>Lvl {listing.item.requiredLevel}</span>
                    )}
                  </div>
                  {listing.item.bonuses && (
                    <div className={styles.itemBonuses}>{listing.item.bonuses}</div>
                  )}
                <div className={styles.itemBadges}>
                    <span className={styles.boundBadge}>Binds on Purchase</span>
                  </div>
                  <div className={styles.itemStats}>
                    {listing.item.minDamage != null && listing.item.maxDamage != null && (
                      <span>DMG: {listing.item.minDamage}–{listing.item.maxDamage}</span>
                    )}
                    {listing.item.dmgCap != null && (
                      <span>Cap: {listing.item.dmgCap}</span>
                    )}
                    {listing.item.blockValue != null && (
                      <span>Block: {listing.item.blockValue}</span>
                    )}
                    {listing.item.breakValue != null && (
                      <span>Dur: {listing.item.breakValue}</span>
                    )}
                    {listing.item.critChance != null && listing.item.critChance > 0 && (
                      <span>Crit: {listing.item.critChance}%</span>
                    )}
                  </div>
                </div>

                <div className={styles.itemPurchase}>
                  <div className={styles.priceDisplay}>
                    <span className={styles.tokenIconSmall}>⚜</span>
                    <span className={styles.price}>{listing.tokenPrice}</span>
                  </div>
                  <button
                    className={`${styles.buyButton} ${(!inStock || !canAfford) ? styles.buyDisabled : ''}`}
                    onClick={() => inStock && canAfford && !isBuying && handleBuy(listing.vaultItemId, listing.item.name, listing.tokenPrice)}
                    disabled={!inStock || !canAfford || isBuying}
                  >
                    {isBuying ? 'Purchasing…' : !inStock ? 'Sold Out' : !canAfford ? 'Not Enough Tokens' : 'Purchase'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.ok ? 'success' : 'error'}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default EmperorsVault;
